import { useEffect, useState, useRef } from "react";
import { useNotifications, Notification } from "../../contexts/NotificationContext";
import { X, Bell, AlertCircle, Calendar, Mail, Info } from "lucide-react";

export default function ToastContainer() {
  const { notifications, markAsRead, soundEnabled } = useNotifications();
  const [visible, setVisible] = useState<Notification[]>([]);
  const processedIds = useRef(new Set<string>());

  useEffect(() => {
    if (notifications.length === 0) return;
    const latest = notifications[0];
    
    // Only show if it's a new unread notification
    if (latest.read) return;
    
    // Skip if we've already processed this notification
    if (processedIds.current.has(latest.id)) return;
    processedIds.current.add(latest.id);
    
    setVisible((prev) => [latest, ...prev]);

    // Play sound only if enabled
    if (soundEnabled) {
      const audio = new Audio("/sounds/notification.wav");
      audio.volume = 0.4;
      audio.play().catch(() => {
        // Silently fail if audio can't play (browser restrictions, file not found, etc.)
      });
    }

    const timer = setTimeout(() => {
      setVisible((prev) => prev.filter((t) => t.id !== latest.id));
    }, 4000);

    return () => clearTimeout(timer);
  }, [notifications, soundEnabled]);

  const handleDismiss = (id: string) => {
    setVisible((prev) => prev.filter((t) => t.id !== id));
    markAsRead(id);
  };

  const getIcon = (type: Notification["type"]) => {
    switch (type) {
      case "appointment":
        return <Calendar className="w-5 h-5" />;
      case "alert":
        return <AlertCircle className="w-5 h-5" />;
      case "reminder":
        return <Bell className="w-5 h-5" />;
      case "message":
        return <Mail className="w-5 h-5" />;
      case "update":
        return <Info className="w-5 h-5" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  const getColor = (type: Notification["type"]) => {
    switch (type) {
      case "appointment":
        return "bg-blue-500";
      case "alert":
        return "bg-red-500";
      case "reminder":
        return "bg-yellow-500";
      case "message":
        return "bg-green-500";
      case "update":
        return "bg-purple-500";
      default:
        return "bg-gray-500";
    }
  };

  if (visible.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-3 max-w-sm w-full pointer-events-none">
      {visible.map((notification) => (
        <div
          key={notification.id}
          className="pointer-events-auto bg-white rounded-xl shadow-lg border border-gray-200 p-4 transform transition-all duration-300 hover:scale-[1.02] animate-slide-in"
        >
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className={`${getColor(notification.type)} text-white p-2 rounded-lg flex-shrink-0`}>
              {getIcon(notification.type)}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h4 className="text-sm font-semibold text-gray-900 truncate">
                  {notification.title}
                </h4>
                <button
                  onClick={() => handleDismiss(notification.id)}
                  className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                  aria-label="Dismiss notification"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                {notification.message}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-gray-500">{notification.time}</span>
                {notification.source !== "General" && (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                    {notification.source}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}